import { NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { CreditCardValidator } from '@curbnturf/cnt-masks';
import { CardExpirationPipe } from '@curbnturf/cnt-masks/src/lib/cc/card-expiration.pipe';
import { CCCvcFormatDirective } from '@curbnturf/cnt-masks/src/lib/cc/directives/cc-cvc-format.directive';
import { CCNumberFormatDirective } from '@curbnturf/cnt-masks/src/lib/cc/directives/cc-number-format.directive';
import { ICCInfo, User } from '@curbnturf/entities';
import {
  addressRequiredFormBuilder,
  DisplayValidationErrorsDirective,
  DisplayValidationErrorsSubmitButtonDirective,
  DisplayValidationErrorsSubmitDirective,
  IAddressForm,
  stringFromError,
} from '@curbnturf/form-helpers';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { LoadingController } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import * as Sentry from '@sentry/angular-ivy';
import { DateTime } from 'luxon';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { CreditCardService } from '../credit-card.service';

@Component({
  selector: 'curbnturf-credit-card-editing',
  templateUrl: './credit-card-editing.component.html',
  styleUrls: ['./credit-card-editing.component.scss'],
  standalone: true,
  imports: [
    CardExpirationPipe,
    CCCvcFormatDirective,
    CCNumberFormatDirective,
    DisplayValidationErrorsDirective,
    DisplayValidationErrorsSubmitButtonDirective,
    DisplayValidationErrorsSubmitDirective,
    FormsModule,
    NgIf,
    NgSelectModule,
    NgFor,
    ReactiveFormsModule,
  ],
})
export class CreditCardEditingComponent implements OnInit {
  user: User;
  ccInfo?: ICCInfo;

  expirationYears: number[] = [];

  replace = false;

  ccForm: FormGroup<{
    cardHolderName: FormControl<string | null>;
    cardNumber: FormControl<string | null>;
    cardExpMonth: FormControl<string | null>;
    cardExpYear: FormControl<number | null>;
    cardCvv: FormControl<string | null>;
  }>;

  addressForm: FormGroup<IAddressForm>;

  get address() {
    if (this.addressForm) {
      return this.addressForm.get('address');
    }

    return;
  }

  get city() {
    if (this.addressForm) {
      return this.addressForm.get('city');
    }

    return;
  }

  get state() {
    if (this.addressForm) {
      return this.addressForm.get('state');
    }

    return;
  }

  get zipcode() {
    if (this.addressForm) {
      return this.addressForm.get('zipcode');
    }

    return '';
  }

  get isCardSaved() {
    return !!(!this.replace && this.ccInfo);
  }

  subscriptions: Subscription[] = [];

  navigationSubject: Subject<boolean>;
  navigation$: Observable<boolean>;

  constructor(
    private fb: FormBuilder,
    private alertFacade: AlertFacade,
    private userFacade: UserFacade,
    private ccService: CreditCardService,
    public loadingController: LoadingController,
  ) {
    this.ccForm = this.fb.group({
      cardHolderName: this.fb.control<string | null>('', [Validators.required]),
      cardNumber: this.fb.control<string | null>('', [Validators.required, CreditCardValidator.validateCardNumber]),
      cardExpMonth: this.fb.control<string | null>(null, [Validators.required]),
      cardExpYear: this.fb.control<number | null>(null, [Validators.required]),
      cardCvv: this.fb.control<string | null>('', [Validators.required, CreditCardValidator.validateCardCvc]),
    });

    this.addressForm = addressRequiredFormBuilder(this.fb);
  }

  ngOnInit() {
    const year = DateTime.now().year;
    for (let i = year; i < year + 13; i++) {
      this.expirationYears.push(i);
    }

    this.navigationSubject = new Subject<boolean>();
    this.navigation$ = this.navigationSubject.asObservable();

    this.subscriptions.push(
      this.userFacade.currentUserOnce$.subscribe((user) => {
        this.user = user;

        if (this.user.getName()) {
          const cardHolderNameForm = this.ccForm.get('cardHolderName');
          if (cardHolderNameForm) {
            cardHolderNameForm.setValue(this.user.getName());
          }
        }

        if (this.user.billing?.zipcode) {
          this.addressForm.patchValue(this.user.billing);
        } else if (this.user.address) {
          this.addressForm.patchValue(this.user.address);
        }

        if (user?.id) {
          this.ccService
            .query(user.id)
            .subscribe((ccInfo: ICCInfo) => (this.ccInfo = ccInfo && ccInfo.number ? ccInfo : undefined));
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  async save() {
    const ccValue = this.ccForm.value;
    const addressValue = this.addressForm.value;

    if (
      !ccValue.cardExpMonth ||
      !ccValue.cardExpYear ||
      !addressValue ||
      !addressValue.zipcode ||
      !addressValue.address ||
      !addressValue.city ||
      !addressValue.state ||
      !ccValue.cardNumber ||
      !ccValue.cardCvv
    ) {
      return;
    }

    this.ccForm.markAsPristine();

    const loading = await this.loadingController.create({
      message: 'Saving Credit Card...',
    });
    await loading.present();

    this.saveCreditCard().subscribe(
      async (ccInfo: ICCInfo) => {
        await loading.dismiss();
        this.ccInfo = ccInfo;
        this.replace = false;
      },
      async (error: HttpErrorResponse | string) => {
        // Attempt to log the error message with Sentry
        try {
          Sentry.configureScope((scope) => scope.setTransactionName('Credit Card Processing Error'));
          Sentry.setContext('Credit Card Processing Error', {
            error,
          });
          Sentry.captureException(error);
        } catch (e) {
          console.error('saveCreditCardError', error, e);
        }

        await loading.dismiss();
        return this.alertFacade.display({
          level: 'error',
          title: 'Error',
          body: `${stringFromError(error)} <br> Please check your Credit Card information and try again.`,
        });
      },
      async () => await loading.dismiss(),
    );
  }

  saveCreditCard(): Observable<ICCInfo | undefined> {
    if (this.user && this.user.id && (this.replace || !this.ccInfo)) {
      const ccValue = this.ccForm.value;
      const addressValue = this.addressForm.value;

      const cardHolder = ccValue.cardHolderName;
      let firstName = '';
      let lastName = '';

      if (cardHolder) {
        const nameArray = cardHolder.split(' ');
        lastName = nameArray.pop() || '';
        firstName = nameArray.join(' ');
      }

      return this.ccService.save(
        {
          userId: this.user.id,
          firstName,
          lastName,
          address: addressValue.address || '',
          city: addressValue.city || '',
          state: addressValue.state || '',
          zipcode: addressValue.zipcode || '',
        },
        {
          ssl_card_number: ccValue?.cardNumber?.replace(/ /g, '') || '',
          ssl_exp_date: ccValue.cardExpMonth + String((ccValue?.cardExpYear || 0) - 2000),
          ssl_cvv2cvc2: ccValue.cardCvv || '',
        },
      );
    }

    return of(this.ccInfo);
  }

  updateFormValidity() {
    for (const x in this.ccForm.controls) {
      if (this.ccForm.controls.hasOwnProperty(x)) {
        (this.ccForm.controls as any)[x].updateValueAndValidity();
      }
    }

    for (const x in this.addressForm.controls) {
      if (this.addressForm.controls.hasOwnProperty(x)) {
        (this.addressForm.controls as any)[x].updateValueAndValidity();
      }
    }
  }
}
